import findIndex from 'lodash-es/findIndex';
import sanitizeSlides from '../../common/actions/data/slides';
import { getHashIndex } from '../../common/lib/urls';
import { migrateJSON } from '../../common/migrations';
import { hasChangesToPublish } from '../../common/selectors/presentationSelectors';

const defaultState = {
  index: 0,
  firebaseInitialized: false,
  transitionsEnabled: false,
  ready: false,
  transitioning: false,
  slides: [],
  user: null,
  team: null,
  updatedAt: '',
  publishedAt: '',
  publishedVersion: '',
  version: '',
  hasChangesToPublish: false,
  canClone: true,
  canDownload: true,
  visible: true,
  symbols: [],
  thumbnail: '',
  hideControls: true,
  scrollableViewer: false,
};

const slideReducer = (state = defaultState, action) => {
  switch (action.type) {
    case 'INIT': {
      const slides = action.slides.map(slide => ({
        ...slide,
        content: slide.content,
      }));
      const symbols = action.symbols.map(symbol => {
        let content = {};
        try {
          content = JSON.parse(symbol.content);
        } catch (e) {
          console.error(e, 'symbol:', symbol);
          content = {};
        }
        return {
          ...symbol,
          content: JSON.stringify(migrateJSON(content)),
        };
      });

      const updatedAt = action.updatedAt
        ? new Date(action.updatedAt).toISOString()
        : action.updatedAt;
      return {
        ...state,
        ready: true,
        title: action.name,
        index: action.index,
        slides: state.slides.length ? state.slides : sanitizeSlides(slides),
        user: action.user,
        team: action.team,
        hideControls: action.hideControls,
        scrollableViewer: action.scrollableViewer,
        updatedAt,
        publishedAt: action.publishedAt,
        hasChangesToPublish: hasChangesToPublish(
          action.publishedAt,
          action.updatedAt,
        ),
        canClone: action.canClone,
        canDownload: action.canDownload,
        visible: action.visible,
        symbols,
      };
    }

    case 'REFRESH_PRESENTATION': {
      const isWindowDraft =
        new URLSearchParams(window.location.search).get('draft') === '1';
      return {
        ...state,
        firebaseInitialized: true,
        hasChangesToPublish: hasChangesToPublish(
          action.props.publishedAt,
          action.props.updatedAt,
        ),
        publishedVersion: action.props.publishedVersion,
        version: action.props.version,
        hideControls: action.props.hideControls,
        scrollableViewer: action.props.scrollableViewer,
        slides: action.props.slides || state.slides,
        title: isWindowDraft ? action.props.name || state.title : state.title,
        index: Math.min(
          state.firebaseInitialized
            ? getHashIndex(
                window.location.hash,
                action.props.slides || state.slides,
              ) - 1
            : state.index,
          (action.props.slides || state.slides).length - 1,
        ),
      };
    }
    case 'REFRESH_SYMBOLS':
      return { ...state, symbols: action.symbols };

    case 'PREVIOUS_SLIDE':
    case 'NEXT_SLIDE':
    case 'GO_TO_SLIDE': {
      let newIndex;
      if (action.type === 'PREVIOUS_SLIDE') {
        newIndex = state.index - 1;
      } else if (action.type === 'NEXT_SLIDE') {
        newIndex = state.index + 1;
      } else {
        newIndex = action.index;
      }
      if (
        newIndex >= 0 &&
        newIndex < state.slides.length &&
        newIndex !== state.index
      ) {
        return {
          ...state,
          index: newIndex,
          transitionsEnabled: !!action.transitionsEnabled,
          slideChangedBy: action.slideChangedBy || null,
        };
      }
      return state;
    }
    case 'TRANSITION_START': {
      return {
        ...state,
        transitioning: true,
      };
    }
    case 'TRANSITION_COMPLETE': {
      return {
        ...state,
        transitioning: false,
      };
    }
    case 'ADD_MEDIA': {
      const index = findIndex(
        state.slides,
        slide => slide.id === action.slideId,
      );
      const currentSlide = state.slides[index];
      const beforeSlides = state.slides.slice(0, index);
      const afterSlides = state.slides.slice(index + 1);
      const newCurrentSlide = {
        ...currentSlide,
        media: action.media,
      };
      const newSlides = [...beforeSlides, newCurrentSlide, ...afterSlides];
      return {
        ...state,
        slides: newSlides,
      };
    }
    case 'DELETE_MEDIA': {
      const index = findIndex(
        state.slides,
        slide => slide.id === action.slideId,
      );
      const currentSlide = state.slides[index];
      const beforeSlides = state.slides.slice(0, index);
      const afterSlides = state.slides.slice(index + 1);
      const newCurrentSlide = {
        ...currentSlide,
        media: null,
      };
      const newSlides = [...beforeSlides, newCurrentSlide, ...afterSlides];
      return {
        ...state,
        slides: newSlides,
      };
    }
    case 'THUMBNAIL_GENERATED': {
      return {
        ...state,
        thumbnail: action.thumbnail,
      };
    }
    case 'PUBLISHED_PRESENTATION': {
      return {
        ...state,
        publishedAt: action.publishedAt,
        hasChangesToPublish: false,
      };
    }
    default:
      return state;
  }
};

export default slideReducer;
